/**
 * Native FHIR CarePlan + Goal mapping for Unify Diagnosis & Treatment Plans.
 *
 * QuestionnaireResponse remains the authoring/signature surface. This module
 * converts {@link ParsedTreatmentPlanDraft} to a transaction of CarePlan + Goals
 * and back. Extract does not write Condition resources.
 */

import type {
  Bundle,
  BundleEntry,
  CarePlan,
  CarePlanActivity,
  CodeableConcept,
  Condition,
  Extension,
  Goal,
  Identifier,
  QuestionnaireResponse,
  Reference,
} from '@medplum/fhirtypes';

import {
  AI_TREATMENT_PLAN_GENERATION_PROVENANCE_EXT,
  EPISODE_OF_CARE_CONTEXT_EXT,
  GOAL_PERTAINS_TO_GOAL_EXT,
  TEMPLATE_CATEGORY_SYSTEM,
  TREATMENT_PLAN_ACTIVITY_CODE_SYSTEM,
  TREATMENT_PLAN_ACTIVITY_FREQUENCY,
  TREATMENT_PLAN_ACTIVITY_INTERVENTION,
  TREATMENT_PLAN_ACTIVITY_SYMPTOM,
  TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM,
  TREATMENT_PLAN_SYMPTOM_NOTE_AUTHOR,
  TREATMENT_PLAN_DIAGNOSIS_INDEX_EXT,
  TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM,
  TREATMENT_PLAN_GOAL_KIND_OBJECTIVE,
  TREATMENT_PLAN_GOAL_KIND_GOAL,
  TREATMENT_PLAN_GOAL_KIND_SYSTEM,
  US_CORE_CAREPLAN_CATEGORY_ASSESS_PLAN,
  US_CORE_CAREPLAN_CATEGORY_SYSTEM,
  WORKFLOW_EPISODE_OF_CARE_EXT,
} from '../constants';
import { withUnifyProfile } from '../profiles/with-unify-profile';
import { getTreatmentPlanGenerationProvenance, parseTreatmentPlanDraft } from './treatment-plan';
import type { ParsedTreatmentPlanDraft } from './treatment-plan';

export function treatmentPlanCarePlanIdentifierValue(qrId: string): string {
  return qrId;
}

export function treatmentPlanGoalIdentifierValue(
  qrId: string,
  diagnosisIndex: number,
  goalIndex: number,
): string {
  return `${qrId}:d${diagnosisIndex}:g${goalIndex}`;
}

export function treatmentPlanObjectiveIdentifierValue(
  qrId: string,
  diagnosisIndex: number,
  goalIndex: number,
  objectiveIndex: number,
): string {
  return `${qrId}:d${diagnosisIndex}:g${goalIndex}:o${objectiveIndex}`;
}

export function isTreatmentPlanCarePlan(carePlan: CarePlan): boolean {
  if (carePlan.identifier?.some((id) => id.system === TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM)) {
    return true;
  }
  return (
    carePlan.meta?.tag?.some(
      (tag) => tag.system === TEMPLATE_CATEGORY_SYSTEM && tag.code === 'treatment-planning',
    ) ?? false
  );
}

export function getCarePlanEpisodeId(carePlan: CarePlan): string | undefined {
  const fromWorkflow = carePlan.extension?.find((e) => e.url === WORKFLOW_EPISODE_OF_CARE_EXT)
    ?.valueReference?.reference;
  if (fromWorkflow?.startsWith('EpisodeOfCare/')) {
    return fromWorkflow.replace(/^EpisodeOfCare\//, '');
  }
  // Legacy Unify EoC context (pre-HL7 workflow extension on CarePlan).
  const fromLegacy = carePlan.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)
    ?.valueReference?.reference;
  if (fromLegacy?.startsWith('EpisodeOfCare/')) {
    return fromLegacy.replace(/^EpisodeOfCare\//, '');
  }
  const fromInfo = carePlan.supportingInfo?.find((r) => r.reference?.startsWith('EpisodeOfCare/'))
    ?.reference;
  return fromInfo?.replace(/^EpisodeOfCare\//, '') || undefined;
}

export function getCarePlanQuestionnaireResponseId(carePlan: CarePlan): string | undefined {
  const fromId = carePlan.identifier?.find((id) => id.system === TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM)
    ?.value;
  if (fromId) return fromId.replace(/^QuestionnaireResponse\//, '');
  const fromInfo = carePlan.supportingInfo?.find((r) => r.reference?.startsWith('QuestionnaireResponse/'))
    ?.reference;
  return fromInfo?.replace(/^QuestionnaireResponse\//, '') || undefined;
}

/** Signed / billable-ready clinical plan (display + due-task current plan). */
export function isActiveTreatmentPlanCarePlan(carePlan: CarePlan): boolean {
  return isTreatmentPlanCarePlan(carePlan) && carePlan.status === 'active';
}

export function isActiveTreatmentPlanCarePlanForEpisode(
  carePlan: CarePlan,
  episodeOfCareId: string,
): boolean {
  return isActiveTreatmentPlanCarePlan(carePlan) && getCarePlanEpisodeId(carePlan) === episodeOfCareId;
}

export function resolveTreatmentPlanCarePlanStatus(input: {
  qrStatus?: string;
  billableReady?: boolean;
}): CarePlan['status'] {
  if (input.qrStatus === 'entered-in-error') return 'entered-in-error';
  if (input.qrStatus === 'stopped') return 'completed';
  if (input.billableReady) return 'active';
  return 'draft';
}

/** @deprecated Read-helper for legacy resources that still carry the index extension. */
function legacyDiagnosisIndexOf(resource: { extension?: Extension[] } | Reference): number | undefined {
  const ext = resource.extension?.find((e) => e.url === TREATMENT_PLAN_DIAGNOSIS_INDEX_EXT);
  return typeof ext?.valueInteger === 'number' ? ext.valueInteger : undefined;
}

function goalKindConcept(kind: string): CodeableConcept {
  return {
    coding: [
      {
        system: TREATMENT_PLAN_GOAL_KIND_SYSTEM,
        code: kind,
        display: kind === TREATMENT_PLAN_GOAL_KIND_OBJECTIVE ? 'Objective' : 'Treatment goal',
      },
    ],
  };
}

function activityCode(code: string, display: string): CodeableConcept {
  return { coding: [{ system: TREATMENT_PLAN_ACTIVITY_CODE_SYSTEM, code, display }] };
}

function activityKind(activity: CarePlanActivity | undefined): string | undefined {
  return activity?.detail?.code?.coding?.find((c) => c.system === TREATMENT_PLAN_ACTIVITY_CODE_SYSTEM)?.code;
}

function goalLifecycleStatus(carePlanStatus: CarePlan['status']): Goal['lifecycleStatus'] {
  switch (carePlanStatus) {
    case 'draft':
      return 'proposed';
    case 'completed':
      return 'completed';
    case 'revoked':
    case 'entered-in-error':
      return 'cancelled';
    case 'on-hold':
      return 'on-hold';
    default:
      return 'active';
  }
}

function pad4(n: number): string {
  return Math.max(0, n).toString(16).padStart(4, '0');
}

function goalUrn(diagnosisIndex: number, goalIndex: number): string {
  // The final UUID group must be exactly 12 hex chars. A shorter group is not a valid
  // UUID, so Medplum skips placeholder substitution and persists the literal urn:uuid
  // into CarePlan.goal — after which every later extract fails resolving that reference.
  return `urn:uuid:a0000000-0000-4000-8000-0000${pad4(diagnosisIndex)}${pad4(goalIndex)}`;
}

function objectiveUrn(diagnosisIndex: number, goalIndex: number, objectiveIndex: number): string {
  return `urn:uuid:b0000000-0000-4000-8000-${pad4(diagnosisIndex)}${pad4(goalIndex)}${pad4(objectiveIndex)}`;
}

const CAREPLAN_URN = 'urn:uuid:c0000000-0000-4000-8000-ffffffffffff';

export interface TreatmentPlanCarePlanExtractInput {
  qr: QuestionnaireResponse;
  carePlanStatus: CarePlan['status'];
  /** Episode-scoped Condition refs aligned with diagnosis repeat index. */
  conditionRefsByDiagnosisIndex?: Array<NonNullable<CarePlan['addresses']>[number] | undefined>;
  careTeamRefs?: CarePlan['careTeam'];
  replaces?: NonNullable<CarePlan['replaces']>[number];
  accounts?: Reference[];
  /** Extra identifiers (for example a source-system record id on historical import). */
  additionalIdentifiers?: Identifier[];
  /** Historical import stores the plan only as a CarePlan, with no QuestionnaireResponse. */
  omitQuestionnaireResponseLink?: boolean;
}

function putEntry(fullUrl: string, resource: Goal | CarePlan, url: string): BundleEntry {
  return {
    fullUrl,
    resource,
    request: { method: 'PUT', url },
  };
}

/**
 * Build a FHIR transaction that upserts one CarePlan and its Goal graph for a
 * primary D&T QuestionnaireResponse.
 */
export function buildTreatmentPlanCarePlanTransaction(
  input: TreatmentPlanCarePlanExtractInput,
): Bundle {
  const qrId = input.qr.id;
  if (!qrId) throw new Error('QuestionnaireResponse.id is required to extract a CarePlan');
  if (!input.qr.subject?.reference) throw new Error('QuestionnaireResponse.subject is required');

  const draft = parseTreatmentPlanDraft(input.qr.item);
  const episodeRef =
    input.qr.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference;
  const goalEntries: BundleEntry[] = [];
  const goalRefs: NonNullable<CarePlan['goal']> = [];
  const activities: CarePlanActivity[] = [];
  const lifecycleStatus = goalLifecycleStatus(input.carePlanStatus);
  const treatmentPlanMeta = {
    tag: [{
      system: TEMPLATE_CATEGORY_SYSTEM,
      code: 'treatment-planning',
      display: 'Treatment Planning',
    }],
    accounts: input.accounts ?? input.qr.meta?.accounts,
  };

  if (draft.treatmentFrequency) {
    activities.push({
      detail: {
        status: 'not-started',
        code: activityCode(TREATMENT_PLAN_ACTIVITY_FREQUENCY, 'Treatment frequency'),
        scheduledString: draft.treatmentFrequency,
        description: draft.treatmentFrequency,
      },
    });
  }

  for (const diagnosis of draft.diagnoses) {
    const di = diagnosis.responseIndex;
    const condition = input.conditionRefsByDiagnosisIndex?.[di];
    const diagnosisDisplay = diagnosis.code ?? `Diagnosis ${di + 1}`;
    const conditionAddress: Reference<Condition> = {
      ...(condition ?? {}),
      display: condition?.display ?? diagnosisDisplay,
    };

    for (const symptom of diagnosis.behavioralDefinitions) {
      if (!symptom.text) continue;
      // Native CarePlan.activity + reasonReference/reasonCode (not a custom diagnosis index).
      activities.push({
        detail: {
          status: 'not-started',
          code: activityCode(TREATMENT_PLAN_ACTIVITY_SYMPTOM, 'Symptom'),
          description: symptom.text,
          reasonReference: condition?.reference ? [conditionAddress] : undefined,
          reasonCode: !condition?.reference && diagnosisDisplay
            ? [{ text: diagnosisDisplay }]
            : undefined,
        },
      });
    }

    for (const goal of diagnosis.goals) {
      const gi = goal.responseIndex;
      const parentUrn = goalUrn(di, gi);
      const parentIdValue = treatmentPlanGoalIdentifierValue(qrId, di, gi);
      const parentGoal: Goal = {
        resourceType: 'Goal',
        lifecycleStatus,
        category: [goalKindConcept(TREATMENT_PLAN_GOAL_KIND_GOAL)],
        description: { text: goal.text ?? '' },
        subject: input.qr.subject as Goal['subject'],
        expressedBy: input.qr.author as Goal['expressedBy'],
        meta: treatmentPlanMeta,
        identifier: [{ system: TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM, value: parentIdValue }],
        addresses: [conditionAddress],
      };
      goalEntries.push(
        putEntry(
          parentUrn,
          withUnifyProfile(parentGoal),
          `Goal?identifier=${TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM}|${parentIdValue}`,
        ),
      );
      goalRefs.push({ reference: parentUrn, display: goal.text });

      for (const objective of goal.objectives) {
        const oi = objective.responseIndex;
        const objUrn = objectiveUrn(di, gi, oi);
        const objIdValue = treatmentPlanObjectiveIdentifierValue(qrId, di, gi, oi);
        const objectiveGoal: Goal = {
          resourceType: 'Goal',
          lifecycleStatus,
          category: [goalKindConcept(TREATMENT_PLAN_GOAL_KIND_OBJECTIVE)],
          description: { text: objective.text ?? '' },
          subject: input.qr.subject as Goal['subject'],
          expressedBy: input.qr.author as Goal['expressedBy'],
          meta: treatmentPlanMeta,
          identifier: [{ system: TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM, value: objIdValue }],
          extension: [
            { url: GOAL_PERTAINS_TO_GOAL_EXT, valueReference: { reference: parentUrn } },
          ],
          addresses: [conditionAddress],
        };
        goalEntries.push(
          putEntry(
            objUrn,
            withUnifyProfile(objectiveGoal),
            `Goal?identifier=${TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM}|${objIdValue}`,
          ),
        );
        goalRefs.push({ reference: objUrn, display: objective.text });
        if (objective.interventions) {
          activities.push({
            detail: {
              status: 'not-started',
              code: activityCode(TREATMENT_PLAN_ACTIVITY_INTERVENTION, 'Intervention'),
              description: objective.interventions,
              goal: [{ reference: objUrn }],
            },
          });
        }
      }
    }
  }

  // Array order is diagnosis rank (no diagnosis-index extension).
  const addresses: NonNullable<CarePlan['addresses']> = draft.diagnoses.map((diagnosis) => {
    const di = diagnosis.responseIndex;
    const condition = input.conditionRefsByDiagnosisIndex?.[di];
    return {
      ...(condition ?? {}),
      display: condition?.display ?? diagnosis.code ?? `Diagnosis ${di + 1}`,
    };
  });

  const supportingInfo: NonNullable<CarePlan['supportingInfo']> = [];
  if (episodeRef?.reference) supportingInfo.push(episodeRef);
  if (!input.omitQuestionnaireResponseLink) {
    supportingInfo.push({ reference: `QuestionnaireResponse/${qrId}` });
  }

  const period =
    draft.assignedAt || draft.expiresAt
      ? { start: draft.assignedAt, end: draft.expiresAt }
      : undefined;

  const extensions: Extension[] = [];
  if (episodeRef) {
    extensions.push({ url: WORKFLOW_EPISODE_OF_CARE_EXT, valueReference: episodeRef });
  }
  const provenance = getTreatmentPlanGenerationProvenance(input.qr);
  if (provenance) {
    extensions.push({
      url: AI_TREATMENT_PLAN_GENERATION_PROVENANCE_EXT,
      valueString: JSON.stringify(provenance),
    });
  }

  const carePlan: CarePlan = {
    resourceType: 'CarePlan',
    status: input.carePlanStatus,
    intent: 'plan',
    title: 'Diagnosis and Treatment Plan',
    description: draft.presentingProblem,
    subject: input.qr.subject as CarePlan['subject'],
    author: input.qr.author,
    created: input.qr.authored,
    period,
    identifier: [
      { system: TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM, value: treatmentPlanCarePlanIdentifierValue(qrId) },
      ...(input.additionalIdentifiers ?? []),
    ],
    category: [
      {
        coding: [
          {
            system: US_CORE_CAREPLAN_CATEGORY_SYSTEM,
            code: US_CORE_CAREPLAN_CATEGORY_ASSESS_PLAN,
            display: 'Assessment and Plan of Treatment',
          },
        ],
      },
    ],
    meta: treatmentPlanMeta,
    instantiatesCanonical: input.qr.questionnaire ? [input.qr.questionnaire] : undefined,
    addresses: addresses.length ? addresses : undefined,
    supportingInfo: supportingInfo.length ? supportingInfo : undefined,
    goal: goalRefs.length ? goalRefs : undefined,
    activity: activities.length ? activities : undefined,
    careTeam: input.careTeamRefs,
    replaces: input.replaces ? [input.replaces] : undefined,
    extension: extensions.length ? extensions : undefined,
  };

  const carePlanIdValue = treatmentPlanCarePlanIdentifierValue(qrId);
  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      ...goalEntries,
      putEntry(
        CAREPLAN_URN,
        withUnifyProfile(carePlan),
        `CarePlan?identifier=${TREATMENT_PLAN_CAREPLAN_IDENTIFIER_SYSTEM}|${carePlanIdValue}`,
      ),
    ],
  };
}

export function carePlanAndGoalsFromTransaction(bundle: Bundle): { carePlan: CarePlan; goals: Goal[] } {
  const goals = (bundle.entry ?? [])
    .map((e) => e.resource)
    .filter((r): r is Goal => r?.resourceType === 'Goal');
  const carePlan = (bundle.entry ?? [])
    .map((e) => e.resource)
    .find((r): r is CarePlan => r?.resourceType === 'CarePlan');
  if (!carePlan) throw new Error('Transaction bundle is missing CarePlan');
  return { carePlan, goals };
}

function goalKind(goal: Goal): string | undefined {
  return goal.category?.flatMap((c) => c.coding ?? []).find((c) => c.system === TREATMENT_PLAN_GOAL_KIND_SYSTEM)
    ?.code;
}

function identifierValue(goal: Goal): string | undefined {
  return goal.identifier?.find((id) => id.system === TREATMENT_PLAN_GOAL_IDENTIFIER_SYSTEM)?.value;
}

function parseGoalIndexes(value: string | undefined): {
  diagnosisIndex: number;
  goalIndex: number;
  objectiveIndex?: number;
} | undefined {
  if (!value) return undefined;
  const match = value.match(/:d(\d+):g(\d+)(?::o(\d+))?$/);
  if (!match) return undefined;
  return {
    diagnosisIndex: Number(match[1]),
    goalIndex: Number(match[2]),
    objectiveIndex: match[3] != null ? Number(match[3]) : undefined,
  };
}

function diagnosisIndexForGoal(goal: Goal): number {
  return parseGoalIndexes(identifierValue(goal))?.diagnosisIndex
    ?? legacyDiagnosisIndexOf(goal)
    ?? 0;
}

function symptomBelongsToDiagnosis(
  activity: CarePlanActivity,
  diagnosisIndex: number,
  address: Reference | undefined,
  diagnosisCount: number,
): boolean {
  const legacy = legacyDiagnosisIndexOf(activity.detail ?? {});
  if (legacy != null) return legacy === diagnosisIndex;

  const reasonRef = activity.detail?.reasonReference?.[0];
  if (reasonRef?.reference && address?.reference) {
    return reasonRef.reference === address.reference;
  }
  if (reasonRef?.display && address?.display) {
    return reasonRef.display === address.display;
  }
  const reasonText = activity.detail?.reasonCode?.[0]?.text;
  if (reasonText && address?.display) {
    return reasonText === address.display;
  }
  // Unscoped symptom activities only attach when there is a single diagnosis.
  return diagnosisCount === 1 && diagnosisIndex === 0;
}

/**
 * Reconstruct a treatment-plan draft from a CarePlan and its Goal resources.
 * Diagnosis grouping uses CarePlan.addresses order + Goal identifiers (`:dN:`),
 * not the retired diagnosis-index extension.
 */
export function parseTreatmentPlanFromCarePlan(
  carePlan: CarePlan,
  goals: Goal[],
  conditions: Condition[] = [],
): ParsedTreatmentPlanDraft {
  const frequencyActivity = (carePlan.activity ?? []).find(
    (a) => activityKind(a) === TREATMENT_PLAN_ACTIVITY_FREQUENCY,
  );
  const treatmentFrequency =
    frequencyActivity?.detail?.scheduledString ?? frequencyActivity?.detail?.description ?? undefined;

  const addresses = carePlan.addresses ?? [];
  const diagnosisIndexes = new Set<number>();
  addresses.forEach((addr, index) => {
    diagnosisIndexes.add(legacyDiagnosisIndexOf(addr) ?? index);
  });
  for (const goal of goals) {
    diagnosisIndexes.add(diagnosisIndexForGoal(goal));
  }
  // Legacy note-only plans.
  for (const note of carePlan.note ?? []) {
    const legacy = legacyDiagnosisIndexOf(note);
    if (legacy != null) diagnosisIndexes.add(legacy);
  }

  const orderedIndexes = [...diagnosisIndexes].sort((a, b) => a - b);
  const diagnosisCount = Math.max(orderedIndexes.length, addresses.length, 1);
  const diagnoses = [];
  for (const di of orderedIndexes) {
    const goalAddress = goals.find((g) => diagnosisIndexForGoal(g) === di)?.addresses?.[0];
    const address = addresses.find((addr, index) => (legacyDiagnosisIndexOf(addr) ?? index) === di)
      ?? (goalAddress?.reference
        ? addresses.find((addr) => addr.reference === goalAddress.reference)
        : undefined)
      ?? goalAddress
      ?? addresses[di];
    const conditionRef = address?.reference;
    const condition = conditions.find(
      (candidate) => candidate.id && conditionRef === `Condition/${candidate.id}`,
    );
    const diagnosisDisplay = address?.display
      ?? goals.find((g) => diagnosisIndexForGoal(g) === di)?.addresses?.[0]?.display
      ?? '';

    const activitySymptoms = (carePlan.activity ?? [])
      .filter((a) => activityKind(a) === TREATMENT_PLAN_ACTIVITY_SYMPTOM)
      .filter((a) =>
        symptomBelongsToDiagnosis(a, di, address ?? { display: diagnosisDisplay }, diagnosisCount),
      )
      .map((a, index) => ({ responseIndex: index, text: a.detail?.description }))
      .filter((symptom) => symptom.text);

    const conditionSymptoms = (condition?.note ?? [])
      .filter((note) => note.authorString === TREATMENT_PLAN_SYMPTOM_NOTE_AUTHOR)
      .map((note, index) => ({ responseIndex: index, text: note.text || undefined }))
      .filter((symptom) => symptom.text);

    // Legacy CarePlan.note snapshots (pre-activity reasonReference).
    const legacyNoteSymptoms = (carePlan.note ?? [])
      .filter(
        (note) =>
          note.authorString === TREATMENT_PLAN_SYMPTOM_NOTE_AUTHOR
          && (legacyDiagnosisIndexOf(note) ?? 0) === di,
      )
      .map((note, index) => ({ responseIndex: index, text: note.text || undefined }))
      .filter((symptom) => symptom.text);

    const symptoms = activitySymptoms.length
      ? activitySymptoms
      : conditionSymptoms.length
        ? conditionSymptoms
        : legacyNoteSymptoms;

    const parentGoals = goals
      .filter((g) => goalKind(g) === TREATMENT_PLAN_GOAL_KIND_GOAL && diagnosisIndexForGoal(g) === di)
      .sort((a, b) => (parseGoalIndexes(identifierValue(a))?.goalIndex ?? 0) - (parseGoalIndexes(identifierValue(b))?.goalIndex ?? 0));

    const parsedGoals = parentGoals.map((parent, gi) => {
      const parentRefCandidates = [
        parent.id ? `Goal/${parent.id}` : undefined,
        goalUrn(di, parseGoalIndexes(identifierValue(parent))?.goalIndex ?? gi),
      ].filter(Boolean) as string[];

      const childObjectives = goals
        .filter((g) => {
          if (goalKind(g) !== TREATMENT_PLAN_GOAL_KIND_OBJECTIVE) return false;
          const pertains = g.extension?.find((e) => e.url === GOAL_PERTAINS_TO_GOAL_EXT)?.valueReference
            ?.reference;
          return Boolean(pertains && parentRefCandidates.includes(pertains));
        })
        .sort(
          (a, b) =>
            (parseGoalIndexes(identifierValue(a))?.objectiveIndex ?? 0) -
            (parseGoalIndexes(identifierValue(b))?.objectiveIndex ?? 0),
        );

      const objectives = childObjectives.map((obj, oi) => {
        const objRefs = [
          obj.id ? `Goal/${obj.id}` : undefined,
          objectiveUrn(di, parseGoalIndexes(identifierValue(parent))?.goalIndex ?? gi, parseGoalIndexes(identifierValue(obj))?.objectiveIndex ?? oi),
        ].filter(Boolean) as string[];
        const intervention = (carePlan.activity ?? []).find(
          (a) =>
            activityKind(a) === TREATMENT_PLAN_ACTIVITY_INTERVENTION &&
            a.detail?.goal?.some((g) => g.reference && objRefs.includes(g.reference)),
        );
        return {
          responseIndex: parseGoalIndexes(identifierValue(obj))?.objectiveIndex ?? oi,
          text: obj.description?.text || undefined,
          interventions: intervention?.detail?.description,
        };
      });

      return {
        responseIndex: parseGoalIndexes(identifierValue(parent))?.goalIndex ?? gi,
        text: parent.description?.text || undefined,
        objectives,
      };
    });

    diagnoses.push({
      responseIndex: di,
      code: diagnosisDisplay || undefined,
      behavioralDefinitions: symptoms,
      goals: parsedGoals,
    });
  }

  return {
    treatmentFrequency,
    presentingProblem: carePlan.description,
    assignedAt: carePlan.period?.start,
    expiresAt: carePlan.period?.end,
    diagnoses: diagnoses.filter(
      (d) => d.code || d.behavioralDefinitions.length || d.goals.length,
    ),
  };
}

export function pickCurrentSignedTreatmentPlanCarePlan(
  plans: CarePlan[],
  episodeOfCareId: string,
): CarePlan | undefined {
  let best: CarePlan | undefined;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const plan of plans) {
    if (!isActiveTreatmentPlanCarePlanForEpisode(plan, episodeOfCareId)) continue;
    const expiresAt = plan.period?.end;
    const ms = expiresAt ? new Date(expiresAt).getTime() : Number.NaN;
    const fallback = plan.created ? new Date(plan.created).getTime() : 0;
    const score = Number.isNaN(ms) ? fallback : ms;
    if (score >= bestMs) {
      bestMs = score;
      best = plan;
    }
  }
  return best;
}

/** @deprecated unused helper kept for tests that inspect urns */
export function treatmentPlanCarePlanUrn(): string {
  return CAREPLAN_URN;
}
